// Modified from https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript

import { Base64Image } from 'common';

/**
 * Convert a base64 string to a Blob
 *
 * @param {string} base64Data - the base64 encoded string
 * @param {string} contentType - the MIME type of the data
 * @returns a Blob object representing the data
 */
export function base64toBlob(base64Data: string, contentType: string): Blob {
  contentType = contentType || '';
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const byteLength = byteCharacters.length;
  const slicesCount = Math.ceil(byteLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, byteLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
}

/**
 * Converts a Blob object to a Base64 string.
 *
 * @param {Blob} blob - The Blob object to be converted.
 * @return {Promise<string>} - A Promise that resolves with the Base64 string.
 */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = event => {
      //Remove the prefix from the base64 string
      const str = event.target.result as string;
      const base64 = str.split(',')[1];
      resolve(base64);
    };
  });
}

export async function blobToBase64ImageUrl(contentType: string, blob: Blob): Promise<string> {
  return `data:${contentType};base64,${await blobToBase64(blob)}`;
}

/**
 * Resizes an image using the given width and height dimensions.
 *
 * @param {Blob} blob - The image file in Blob format.
 * @param {number} width - The desired width of the resized image.
 * @param {number} height - The desired height of the resized image.
 * @param {string} content_type - The MIME type of the resized image. Default is 'image/webp'.
 * @param {number} resize_quality - The quality of the resized image. Default is 0.85.
 * @return {Promise<Blob>} - A Promise that resolves with the resized image in Blob format.
 */
export function resizeImage(blob: Blob, maxWidth: number, maxHeight: number, content_type: string = 'image/webp', resize_quality: number = 0.85): Promise<Blob> {
  return new Promise(resolve => {
    const img = document.createElement('img');
    img.src = URL.createObjectURL(blob);
    img.onload = () => {
      console.log('Image loaded', img.width, img.height);
      let width = img.width;
      let height = img.height;

      if (width >= height && width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
      } else if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, (width / img.width) * img.height); // don't distort image
      canvas.toBlob(resolve, content_type, resize_quality);
    };
  });
}

export function resizeBase64(src: string, maxWidth: number, maxHeight: number): Promise<Base64Image> {
  return new Promise(resolve => {
    const img = document.createElement('img');
    img.src = src;
    img.onload = () => {
      console.log('Image loaded', img.width, img.height);
      let width = img.width;
      let height = img.height;

      if (width >= height && width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
      } else if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, (width / img.width) * img.height); // don't distort image
      const base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1]; // Convert to Base64
      resolve({ mimeType: 'image/jpeg', data: base64, width, height });
    };
  });
}

/**
 * Verifies if the given string is a valid email address.
 *
 * @param {string} email - The email address to be verified.
 * @returns {boolean} - True if the email is valid, false otherwise.
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Emails can't be longer than 254 characters.
  if (email.length > 254) {
    return false;
  }

  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  
  return emailRegex.test(email);
}
